import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Link } from "react-router"
import { inquiryStatusLabel } from "../../../../../api/supplierInquiriesApi"
import { formatInt, localeForLanguage } from "../supplierDashboardStore"
import type { DashboardWidgetProps } from "../widgetRegistry"
import { DashboardRow } from "./DashboardRow"

const MC_BASE = "/material-compliance"

export function OutstandingWidget({ stats, loadFailed }: DashboardWidgetProps) {
  const { translate, currentLanguage } = useTranslation()
  const locale = localeForLanguage(currentLanguage)
  // "ureview" gets its own dashboard-row copy (distinct from the inquiry
  // list's pill label) — the row is the reply itself, so the label says what
  // to do with it ("review it") rather than restating the pill's state.
  const statusLabel = (status: string) =>
    status === "ureview"
      ? translate("MC_SSD_OUT_STATE_UREVIEW")
      : inquiryStatusLabel(status, translate)
  if (stats.outstandingTop.length === 0) {
    // After a failed load the list is unknown, not empty: no "nothing here".
    if (loadFailed) return null
    return (
      <div className="py-6 text-sm text-grey5">
        {translate("MC_SSD_EMPTY_LIST")}
      </div>
    )
  }
  return (
    <div>
      {stats.outstandingTop.map((item) => {
        const overdue = item.overdue
        const detailTo = `${MC_BASE}/supplier-studio/inquiry?id=${item.inquiryId}`
        return (
          <DashboardRow
            key={item.inquiryId}
            title={
              <Link
                to={detailTo}
                className="font-semibold text-white underline decoration-grey5 underline-offset-4 hover:decoration-white"
              >
                {item.supplierName ?? item.inquiryId}
              </Link>
            }
            metric={
              <span className={cn(overdue && "text-redLight")}>
                {overdue
                  ? item.overdueDays > 0
                    ? translate("MC_SSD_ROW_OVERDUE", {
                        days: item.overdueDays,
                      })
                    : // F3: overdue=true with overdueDays=0 is ambiguous — either
                      // the deadline itself elapsed today, or a reminder fired
                      // (reminderCount>0) and pushed due_at forward, so the
                      // deadline hasn't actually elapsed. "Overdue today" would
                      // be false in the reminded case; use the reminded copy
                      // instead, keyed off waitingDays (the honest age).
                      item.reminderCount > 0
                      ? translate("MC_SSD_ROW_OVERDUE_REMINDED", {
                          days: item.waitingDays,
                        })
                      : translate("MC_SSD_ROW_OVERDUE_TODAY")
                  : translate("MC_SSD_ROW_WAITING", { days: item.waitingDays })}
              </span>
            }
            metricLabel={`${formatInt(item.articlesLinked, locale)} ${translate("MC_SSD_ROW_ARTICLES_LINKED")}`}
            action={
              overdue
                ? { label: translate("MC_SSD_ROW_DECIDE"), to: detailTo }
                : undefined
            }
            stateLabel={overdue ? undefined : statusLabel(item.status)}
          />
        )
      })}
    </div>
  )
}
